import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useRoute } from "wouter";
import { AlertTriangle, ArrowLeft, ArrowRight, CheckCircle2, RotateCcw, Wrench } from "lucide-react";
import { AppLayout } from "@/components/layout/AppLayout";
import { Badge, Card, PrimaryButton, SectionTitle } from "@/components/Card";
import { useDemoStore } from "@/store/useDemoStore";
import {
  useCandidatesList,
  useEffectiveUser,
  useEnrollmentsForCandidate,
  useExpedienteForCandidateStandard,
  useLmsRoute,
  usePreopRuns,
} from "@/store/hooks";
import { LMS_PREOP_ITEMS_EC1781, type PreopEstado } from "@/data/seed";

type State = { estado?: PreopEstado; observacion?: string };

export default function PreopSimulator() {
  const setRole = useDemoStore((s) => s.setRole);
  useEffect(() => setRole("alumno"), [setRole]);
  const recordPreopRun = useDemoStore((s) => s.recordPreopRun);

  const [, params] = useRoute("/alumno/ruta/simulador/:moduloId");
  const moduloId = params?.moduloId ?? "m6";
  const [, navigate] = useLocation();

  const effective = useEffectiveUser();
  const candList = useCandidatesList();
  const candidateId = effective && effective.roles.includes("alumno") ? effective.id : candList[0]?.id;
  const enrollments = useEnrollmentsForCandidate(candidateId);
  const codigo = enrollments.find((c) => c === "EC1781") ?? "EC1781";
  const exp = useExpedienteForCandidateStandard(candidateId, codigo);
  const route = useLmsRoute(codigo);
  const runs = usePreopRuns(exp?.id);
  const rawMod = route?.modules.find((m) => m.id === moduloId);
  useEffect(() => {
    if (rawMod && rawMod.kind !== "simulador-preop") {
      navigate(`/alumno/ruta/modulo/${rawMod.id}`, { replace: true });
    }
  }, [rawMod, navigate]);
  const mod = rawMod && rawMod.kind === "simulador-preop" ? rawMod : undefined;
  const nextMod = useMemo(() => {
    if (!route || !mod) return undefined;
    const idx = route.modules.findIndex((m) => m.id === mod.id);
    return route.modules[idx + 1];
  }, [route, mod]);

  const items = LMS_PREOP_ITEMS_EC1781;
  const [estados, setEstados] = useState<Record<string, State>>({});
  const [submitted, setSubmitted] = useState(false);
  const [result, setResult] = useState<{ aprobado: boolean; completas: number; total: number; criticasFaltantes: number } | null>(null);

  const completas = items.filter((i) => estados[i.id]?.estado === "cumple" || estados[i.id]?.estado === "no-aplica").length;
  const criticasFaltantes = items.filter((i) => {
    if (!i.critical) return false;
    const s = estados[i.id];
    return !s?.estado || s.estado === "no-cumple";
  }).length;
  const evaluados = items.filter((i) => !!estados[i.id]?.estado).length;
  const allEvaluated = evaluados === items.length;

  if (!exp || !route || !mod) {
    return (
      <AppLayout pageTitle="Simulador">
        <Card>
          <div style={{ padding: 24, textAlign: "center", color: "var(--cc-gray-600)" }}>
            Simulador no disponible. <Link href="/alumno/ruta">Volver a la ruta</Link>.
          </div>
        </Card>
      </AppLayout>
    );
  }

  const onSubmit = () => {
    const entries = items
      .filter((i) => !!estados[i.id]?.estado)
      .map((i) => ({
        itemId: i.id,
        estado: estados[i.id]!.estado!,
        observacion: estados[i.id]?.observacion,
      }));
    const r = recordPreopRun(exp.id, mod.id, entries, candidateId ?? undefined);
    setResult({ aprobado: r.aprobado, completas: r.completas, total: r.total, criticasFaltantes: r.criticasFaltantes });
    setSubmitted(true);
  };
  const onReset = () => {
    setEstados({});
    setSubmitted(false);
    setResult(null);
  };

  return (
    <AppLayout pageTitle={`Simulador · ${mod.titulo}`}>
      <div style={{ marginBottom: 14 }}>
        <Link href="/alumno/ruta" data-testid="link-back-ruta">
          <PrimaryButton variant="ghost" size="sm"><ArrowLeft size={12} /> Volver a la ruta</PrimaryButton>
        </Link>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) 300px", gap: 16, alignItems: "start" }}>
        <Card testId="preop-simulator">
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
            <Wrench size={16} color="var(--cc-teal)" />
            <Badge tone="navy">Simulador preoperativo</Badge>
          </div>
          <h2 style={{ fontSize: 18, fontWeight: 700, color: "var(--cc-navy)", marginBottom: 6 }}>{mod.titulo}</h2>
          <p style={{ fontSize: 12, color: "var(--cc-gray-600)", marginBottom: 14 }}>
            Para cada punto seleccione <strong>cumple</strong>, <strong>no cumple</strong> o <strong>no aplica</strong>. Los puntos críticos no pueden quedar en “no cumple”. Necesita además ≥80% de puntos cumplidos o no aplicables.
          </p>

          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {items.map((it) => {
              const cur = estados[it.id] ?? {};
              const tone = cur.estado === "cumple" ? "var(--cc-green)"
                : cur.estado === "no-cumple" ? "var(--cc-red)"
                : cur.estado === "no-aplica" ? "var(--cc-gray-400)" : "var(--cc-gray-100)";
              return (
                <div
                  key={it.id}
                  data-testid={`preop-item-${it.id}`}
                  style={{
                    padding: "10px 12px", borderRadius: 8,
                    background: "white",
                    border: `1px solid ${tone}`,
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
                    <div style={{ flex: 1, fontSize: 13, color: "var(--cc-gray-800)" }}>{it.label}</div>
                    {it.critical && <Badge tone="red">Crítico</Badge>}
                  </div>
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                    {(["cumple", "no-cumple", "no-aplica"] as PreopEstado[]).map((e) => {
                      const active = cur.estado === e;
                      const c = e === "cumple" ? { fg: "var(--cc-green)", bg: "var(--cc-green-bg)" }
                        : e === "no-cumple" ? { fg: "var(--cc-red)", bg: "var(--cc-red-bg)" }
                        : { fg: "var(--cc-gray-600)", bg: "var(--cc-gray-100)" };
                      return (
                        <button
                          key={e}
                          disabled={submitted}
                          onClick={() => setEstados((prev) => ({ ...prev, [it.id]: { ...prev[it.id], estado: e } }))}
                          data-testid={`preop-${it.id}-${e}`}
                          style={{
                            padding: "5px 10px", fontSize: 11, fontWeight: 700,
                            border: `1.5px solid ${active ? c.fg : "var(--cc-gray-200)"}`,
                            background: active ? c.bg : "white",
                            color: active ? c.fg : "var(--cc-gray-600)",
                            borderRadius: 6, cursor: submitted ? "default" : "pointer",
                            textTransform: "uppercase", letterSpacing: "0.04em",
                          }}
                        >
                          {e === "no-cumple" ? "No cumple" : e === "no-aplica" ? "No aplica" : "Cumple"}
                        </button>
                      );
                    })}
                  </div>
                  {cur.estado && cur.estado !== "cumple" && (
                    <textarea
                      disabled={submitted}
                      value={cur.observacion ?? ""}
                      onChange={(e) => setEstados((prev) => ({ ...prev, [it.id]: { ...prev[it.id], observacion: e.target.value } }))}
                      placeholder="Observación (opcional)"
                      data-testid={`preop-obs-${it.id}`}
                      style={{
                        marginTop: 6, width: "100%", minHeight: 36, padding: "6px 8px",
                        border: "1px solid var(--cc-gray-200)", borderRadius: 6,
                        fontSize: 12, color: "var(--cc-gray-800)", fontFamily: "inherit", resize: "vertical",
                      }}
                    />
                  )}
                </div>
              );
            })}
          </div>

          <div style={{ marginTop: 14, display: "flex", gap: 8, justifyContent: "flex-end" }}>
            {!submitted ? (
              <PrimaryButton onClick={onSubmit} disabled={!allEvaluated} testId="button-submit-preop">
                Enviar inspección <ArrowRight size={12} />
              </PrimaryButton>
            ) : (
              <>
                <PrimaryButton variant="outline" onClick={onReset} testId="button-reset-preop">
                  <RotateCcw size={12} /> Reintentar
                </PrimaryButton>
                {result?.aprobado && nextMod && (
                  <PrimaryButton onClick={() => navigate(nextMod.kind === "simulador-preop" ? `/alumno/ruta/simulador/${nextMod.id}` : `/alumno/ruta/modulo/${nextMod.id}`)} testId="button-next-after-preop">
                    Siguiente módulo <ArrowRight size={12} />
                  </PrimaryButton>
                )}
              </>
            )}
          </div>
        </Card>

        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <Card>
            <SectionTitle>Estado actual</SectionTitle>
            <div style={{ fontSize: 13, color: "var(--cc-gray-700)" }}>
              Evaluados: <strong>{evaluados}/{items.length}</strong>
            </div>
            <div style={{ fontSize: 13, color: "var(--cc-gray-700)", marginTop: 2 }}>
              Cumple/N-A: <strong>{completas}/{items.length}</strong>
            </div>
            <div style={{ fontSize: 13, color: criticasFaltantes > 0 ? "var(--cc-red)" : "var(--cc-green)", marginTop: 4 }}>
              {criticasFaltantes > 0 ? (
                <><AlertTriangle size={12} /> {criticasFaltantes} críticas sin cumplir</>
              ) : (
                <><CheckCircle2 size={12} /> Sin críticas pendientes</>
              )}
            </div>
            {!allEvaluated && !submitted && (
              <div style={{ marginTop: 8, fontSize: 11, color: "var(--cc-gray-500)" }}>
                Marque cada punto para poder enviar la inspección.
              </div>
            )}
            {result && (
              <div data-testid="preop-result" style={{ marginTop: 10, paddingTop: 10, borderTop: "1px solid var(--cc-gray-100)" }}>
                <Badge tone={result.aprobado ? "green" : "amber"}>
                  {result.aprobado ? "✓ Inspección aprobada" : "Inspección no aprobada"}
                </Badge>
                <div style={{ marginTop: 6, fontSize: 11, color: "var(--cc-gray-600)" }}>
                  Registrada en el expediente y en la bitácora.
                </div>
              </div>
            )}
          </Card>

          {runs.length > 0 && (
            <Card>
              <SectionTitle>Intentos previos</SectionTitle>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {[...runs].reverse().slice(0, 5).map((r) => (
                  <div key={r.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 11, color: "var(--cc-gray-700)" }}>
                    <span>{new Date(r.fecha).toLocaleString("es-MX")}</span>
                    <Badge tone={r.aprobado ? "green" : "amber"}>{r.completas}/{r.total}{r.criticasFaltantes > 0 ? ` · ${r.criticasFaltantes} críticas` : ""}</Badge>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </div>
      </div>
    </AppLayout>
  );
}
